import { useEffect } from 'react';
import { useAppSelector } from './useAppSelector';
import { useCompanyDetails } from './useCompanyDetails';

interface UseVersionsOptions {
  slug: string;
  enabled?: boolean;
  forceRefresh?: boolean;
}

/**
 * This hook is now a wrapper around useCompanyDetails for backward compatibility.
 * It returns versions data from the Redux store that is populated by useCompanyDetails.
 */
export const useVersions = ({ slug, enabled = true, forceRefresh = false }: UseVersionsOptions) => {
  // Use the integrated company details hook which now handles versions
  const { versionsLoading, versionsError } = useCompanyDetails({ 
    slug, 
    enabled, 
    forceRefresh 
  });
  
  // Get versions from Redux store
  const versions = useAppSelector(state => state.versions.versions);
  const currentSlug = useAppSelector(state => state.versions.currentSlug);
  const storeLoading = useAppSelector(state => state.versions.loading);
  const storeError = useAppSelector(state => state.versions.error);

  // Log versions data for debugging
  useEffect(() => {
    if (versions.length > 0) {
      console.log(`📊 [useVersions] Versions from store for ${slug}:`, versions);
    } else {
      console.log(`📊 [useVersions] No versions in store for ${slug}`);
    }
  }, [versions, slug]);

  return { 
    versions, 
    loading: versionsLoading || storeLoading, 
    error: versionsError || storeError,
    currentSlug
  };
};
